import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/base/Seo";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import FaqAccordion from "@/components/feature/FaqAccordion";
import PostCard from "@/pages/blog/components/PostCard";
import RecentPosts from "@/pages/blog/components/RecentPosts";
import BlogNewsletter from "@/pages/blog/components/BlogNewsletter";
import CategoryHero from "@/pages/blog/components/CategoryHero";
import {
  contentCategories,
  getPostsByCategory,
  type BlogCategory,
} from "@/pages/blog/blogData";

export default function CategoryView({ category }: { category: BlogCategory }) {
  const categoryPosts = getPostsByCategory(category.slug);
  const [lead, ...rest] = categoryPosts;

  const seoTitle = category.collection
    ? "Guias e Tutoriais de Tecnologia | Blog NXHOST"
    : `${category.name}: Guias, Dicas e Tutoriais | Blog NXHOST`;

  return (
    <SiteLayout>
      <Seo
        title={seoTitle}
        description={category.longDescription}
        keywords={[category.name, ...category.topics, "blog nxhost", "tutoriais"].join(", ")}
        path={`/blog/${category.slug}`}
        image={category.ogImage}
        faqs={category.faqs}
        breadcrumbs={[
          { name: "Início", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: category.name, path: `/blog/${category.slug}` },
        ]}
      />

      <CategoryHero category={category} count={categoryPosts.length} />

      <section className="bg-background-50 py-8">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
              Categorias:
            </span>
            {contentCategories.map((item) => (
              <Link
                key={item.slug}
                to={`/blog/${item.slug}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  item.slug === category.slug
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                <i className={item.icon} aria-hidden="true"></i>
                {item.shortName}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {lead ? (
        <section className="bg-background-50 pb-4">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <SectionHeading
              align="left"
              badge="Artigo em destaque"
              badgeIcon="ri-star-line"
              title="Destaque desta categoria"
            />
            <div className="mt-8">
              <Reveal>
                <PostCard post={lead} variant="featured" />
              </Reveal>
            </div>
          </div>
        </section>
      ) : null}

      {rest.length > 0 ? (
        <RecentPosts
          title={category.collection ? "Todos os guias e tutoriais" : `Mais em ${category.name}`}
          description="Conteúdos organizados para você aprender passo a passo, do básico ao avançado."
          posts={categoryPosts}
        />
      ) : null}

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            align="left"
            badge="Subcategorias"
            badgeIcon="ri-price-tag-3-line"
            badgeTone="accent"
            title="Subcategorias e temas"
            description="Clique em um tema para ver os conteúdos relacionados."
          />
          <div className="mt-8 flex flex-wrap gap-2">
            {category.topics.map((topic) => (
              <Link
                key={topic}
                to={`/blog/busca?q=${encodeURIComponent(topic)}`}
                className="inline-flex items-center gap-2 rounded-full border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-hashtag text-foreground-400" aria-hidden="true"></i>
                {topic}
              </Link>
            ))}
          </div>

          {categoryPosts.length === 0 ? (
            <p className="mt-8 text-sm text-foreground-500">
              Ainda não há artigos publicados nesta categoria. Explore outras categorias do blog.
            </p>
          ) : null}
        </div>
      </section>

      {category.faqs && category.faqs.length > 0 ? (
        <section className="bg-background-100 py-14 md:py-20">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <SectionHeading
              badge="Perguntas frequentes"
              badgeIcon="ri-question-line"
              badgeTone="secondary"
              title={`Dúvidas sobre ${category.name}`}
              description="Respostas rápidas para as perguntas mais comuns sobre este tema."
            />
            <div className="mt-10">
              <FaqAccordion items={category.faqs} />
            </div>
          </div>
        </section>
      ) : null}

      <BlogNewsletter />
    </SiteLayout>
  );
}